import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ReportStatus } from '@/graphql/types';
import type {
    DownloadsGraphReportsQuery,
    DownloadsGraphReportsQueryVariables,
    GraphReportsTypeFieldsFragment,
} from '@/graphql/types';
import { updateReadyReportsCount } from '../../utils';
import { createAfterCursor } from '../../../utils/relay';
import { presignedURLRefreshInterval } from '../../../constants';
import type { UseOrderAndPaginationControlReturn } from '../../table/hooks';

const GRAPH_REPORTS_FRAGMENT = gql`
    fragment GraphReportsTypeFields on GraphReportType {
        id
        url
        name
        lastDownloaded
        status
        size
        category
    }
`;

const GRAPH_REPORTS = gql`
    query downloadsGraphReports(
        $orderBy: String
        $before: String
        $after: String
        $first: Int
        $last: Int
        $ids: [ID]
    ) {
        graphReports(
            orderBy: $orderBy
            before: $before
            after: $after
            first: $first
            last: $last
            ids: $ids
        ) {
            readyReportsCount
            totalCount
            edges {
                node {
                    ...GraphReportsTypeFields
                }
            }
            pageInfo {
                startCursor
                endCursor
            }
        }
    }
    ${GRAPH_REPORTS_FRAGMENT}
`;

const UPDATE_LAST_DOWNLOAD = gql`
    mutation downloadsUpdateGraphReportLastDownloaded($id: ID!) {
        updateGraphReportLastDownloaded(id: $id) {
            graphReport {
                ...GraphReportsTypeFields
            }
        }
    }
    ${GRAPH_REPORTS_FRAGMENT}
`;

function createQueryVariables({
    currentPage,
    pageSize,
    orderBy,
}: UseOrderAndPaginationControlReturn) {
    return {
        first: pageSize,
        after: createAfterCursor(currentPage, pageSize),
        orderBy,
    };
}

// Poll for new presigned URLs almost every 1 hour.
const reportURLPollInterval = presignedURLRefreshInterval;
// Poll for new status every 5 seconds.
const reportStatusPollInterval = 5000;

interface GraphReportsEdge {
    node: GraphReportsTypeFieldsFragment;
}

interface UseGraphReportQueryGeneratorResult {
    graphReports: GraphReportsEdge[];
    updateLastDownloadMutation: (options?: { variables: { id: string } }) => Promise<unknown>;
    loading: boolean;
    updating: boolean;
}

export default function useGraphReportQueryGenerator(
    orderAndPaginationControl: UseOrderAndPaginationControlReturn
): UseGraphReportQueryGeneratorResult {
    const [graphReports, setGraphReports] = useState<GraphReportsEdge[]>([]);

    const queryVariables = useMemo(
        () => createQueryVariables(orderAndPaginationControl),
        [orderAndPaginationControl]
    );

    const isPolling = useRef(false);
    const pollInterval = useRef(reportStatusPollInterval);

    const { data, loading, startPolling, stopPolling, networkStatus, refetch } = useQuery<
        DownloadsGraphReportsQuery,
        DownloadsGraphReportsQueryVariables
    >(GRAPH_REPORTS, {
        variables: queryVariables,
        fetchPolicy: 'network-only',
    });

    const [updateLastDownloadMutation] = useMutation<unknown, { id: string }>(
        UPDATE_LAST_DOWNLOAD,
        {
            onCompleted() {
                void refetch();
            },
        }
    );

    useEffect(() => {
        const graphReportsData = data?.graphReports;
        if (!graphReportsData) {
            return;
        }

        updateReadyReportsCount(graphReportsData.readyReportsCount ?? 0);
        setGraphReports((graphReportsData.edges as GraphReportsEdge[]) ?? []);
        orderAndPaginationControl.setTotalCount(graphReportsData.totalCount ?? 0);
    }, [data, orderAndPaginationControl]);

    // Adapts polling interval based on report status: faster if reports are in progress.
    useEffect(() => {
        const shouldPollForReportStatus = graphReports.some(
            (edge) => edge.node.status === ReportStatus.InProgress
        );

        const newInterval = shouldPollForReportStatus
            ? reportStatusPollInterval
            : reportURLPollInterval;

        if (!isPolling.current) {
            startPolling(newInterval);
            isPolling.current = true;
            pollInterval.current = newInterval;
            return;
        }

        if (newInterval !== pollInterval.current) {
            stopPolling();
            startPolling(newInterval);
            pollInterval.current = newInterval;
        }
    }, [graphReports, startPolling, stopPolling]);

    useEffect(
        () => () => {
            if (isPolling.current) {
                stopPolling();
                isPolling.current = false;
            }
        },
        [stopPolling]
    );

    return {
        graphReports,
        updateLastDownloadMutation,
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
    };
}
